import React, { useMemo, useState } from "react";
import { Slate, Editable, withReact } from "slate-react";
import { createEditor } from "slate";
import { withHistory } from "slate-history";
import { Box, Typography } from "@mui/material";
import EditorToolbar from "./EditorToolbar";

const initialValue = [
  {
    type: "paragraph",
    children: [{ text: "Start writing your blog..." }],
  },
];

// 🔹 Deep clone helper
const cloneValue = (value) => JSON.parse(JSON.stringify(value));

const SlateEditor = ({ meta }) => {
  const editor = useMemo(() => withHistory(withReact(createEditor())), []);
  const previewEditor = useMemo(
    () => withHistory(withReact(createEditor())),
    [],
  );

  const [value, setValue] = useState(initialValue);

  const buildFinalJSON = () => {
  const blog = {
    id: Date.now(),
    documentId: crypto.randomUUID(),
    Title: meta.title,
    slug: meta.slug,
    description: meta.description,
    seo_title: null,
    seo_tags: null,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    publishedAt: new Date().toISOString(),
    Content: [
      {
        "__component": "collection.content",
        "Alignment": "Left",
        "BlogContent": value,
        "Image": null
      }
    ]
  };

  return {
    AllData: [
      {
        data: [blog],
        meta: {
          pagination: {
            page: 1,
            pageSize: 1,
            pageCount: 1,
            total: 1
          }
        }
      }
    ]
  };
};

  /* ================= INLINE STYLES ================= */
  const renderLeaf = ({ attributes, children, leaf }) => {
    if (leaf.bold) children = <strong>{children}</strong>;
    if (leaf.italic) children = <em>{children}</em>;
    if (leaf.underline) children = <u>{children}</u>;

    return (
      <span
        {...attributes}
        style={{
          fontSize: leaf.fontSize || "14px",
          color: leaf.color || "inherit",
        }}
      >
        {children}
      </span>
    );
  };

  /* ================= BLOCK STYLES ================= */
  const renderElement = ({ attributes, children, element }) => {
    if (element.type === "image") {
      return (
        <div
          {...attributes}
          contentEditable={false}
          style={{
            textAlign: element.align || "center",
            margin: "16px 0",
          }}
        >
          <img
            src={element.url}
            alt=""
            style={{
              width: element.width || "100%",
              maxWidth: "100%",
            }}
          />
          {children}
        </div>
      );
    }

    if (element.type === "mediaText") {
      const isImageLeft = element.layout === "image-left";

      return (
        <div
          {...attributes}
          style={{
            display: "flex",
            gap: "16px",
            alignItems: "flex-start",
            margin: "16px 0",
            flexDirection: isImageLeft ? "row" : "row-reverse",
          }}
        >
          {/* IMAGE — NOT EDITABLE */}
          <div contentEditable={false} style={{ width: element.image.width }}>
            <img
              src={element.image.url}
              alt=""
              style={{
                width: "100%",
                borderRadius: "6px",
                display: "block",
              }}
            />
          </div>

          {/* TEXT — EDITABLE */}
          <div style={{ flex: 1 }}>{children}</div>
        </div>
      );
    }

    const style = {
      textAlign: element.align || "left",
      paddingTop: element.style?.paddingTop || "0px",
      paddingBottom: element.style?.paddingBottom || "0px",
      paddingLeft: element.style?.paddingLeft || "0px",
      paddingRight: element.style?.paddingRight || "0px",
      marginTop: element.style?.marginTop || "0px",
      marginBottom: element.style?.marginBottom || "16px",
    };

    return (
      <p {...attributes} style={style}>
        {children}
      </p>
    );
  };

  return (
    <Box>
      {/* ================= EDITOR ================= */}
      <Box
        sx={{
          border: "1px solid #ccc",
          borderRadius: "8px",
          padding: "12px",
          background: "#fff",
        }}
      >
        <Slate editor={editor} initialValue={value} onChange={setValue}>
          <EditorToolbar />

          <Editable
            renderLeaf={renderLeaf}
            renderElement={renderElement}
            placeholder="Write something amazing…"
            style={{
              minHeight: "300px",
              outline: "none",
              padding: "8px",
            }}
          />
        </Slate>
      </Box>

      {/* ================= LIVE PREVIEW ================= */}
      <Box
        sx={{
          mt: 4,
          borderRadius: "8px",
          padding: "16px",
          background: "#181818",
          color: "#ffffff",
        }}
      >
        <Typography variant="h6" sx={{ mb: 2 }}>
          Live Preview
        </Typography>

        <Slate
          key={JSON.stringify(value)} // 🔑 FORCE REMOUNT
          editor={previewEditor}
          initialValue={cloneValue(value)} // ✅ REQUIRED
        >
          <Editable
            readOnly
            renderLeaf={renderLeaf}
            renderElement={renderElement}
            style={{
              minHeight: "200px",
              padding: "8px",
            }}
          />
        </Slate>
      </Box>
      <Box sx={{ mt: 4, py: 3, px: 1 }}>
        <button
          variant="contained"
          size="small"
          sx={{ mt: 10, p: 3 }}
          onClick={() => {
            navigator.clipboard.writeText(
              JSON.stringify(buildFinalJSON(), null, 2),
            );
          }}
        >
          Copy JSON
        </button>
      </Box>
      <Box
        sx={{
          mt: 0,
          p: 2,
          background: "#0f172a",
          color: "#e5e7eb",
          borderRadius: "8px",
          fontSize: "13px",
          overflow: "auto",
          maxHeight: "400px",
        }}
      >
        <pre>{JSON.stringify(buildFinalJSON(), null, 2)}</pre>
      </Box>
    </Box>
  );
};

export default SlateEditor;
